import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Button, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ContentCopy, Link, FileDownload } from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

function ContactList() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { id } = useParams();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false);

  useEffect(() => {
    fetchContacts();
    fetchUsers();
    fetchCampaignName();
  }, [activeTab, id]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/contacts/${id}/${activeTab}`);
      setContacts(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      const filteredUsers = response.data.filter(user => user.role !== 'superadmin' && user.role !== 'super_admin');
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Fetch users error:', error);
    }
  };

  const fetchCampaignName = async () => {
    try {
      const response = await api.get('/contacts/campaigns');
      const campaign = response.data.data.find(c => c._id === id);
      if (campaign) {
        setCampaignName(campaign.campaignName);
      }
    } catch (error) {
      console.error('Fetch campaign error:', error);
    }
  };

  const handleCopyTable = () => {
    const headers = ['S.No', 'Name', 'Username', 'Phone Number', 'Email Address', 'Pin Code', 'Instagram Profile Link', 'YouTube Profile Link', 'Market', 'State', 'Remarks'];
    const rows = contacts.map((c, index) => [
      index + 1,
      c.name,
      c.username || '-',
      c.mobile || '-',
      c.businessEmail || '-',
      c.pinCode || '-',
      c.profileLink || '-',
      c.youtubeLink || '-',
      c.market || '-',
      c.state || '-',
      c.remark || '-'
    ]);
    const text = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Table copied to clipboard!');
  };

  const handleCopyLink = () => {
    const link = `${process.env.REACT_APP_BASE_URL}/#/contact-form/${id}`;
    navigator.clipboard.writeText(link);
    toast.success('Form link copied to clipboard!');
  };

  const exportToExcel = () => {
    try {
      const data = contacts.map((c, index) => ({
        'S.No': index + 1,
        'Name': c.name,
        'Username': c.username || '-',
        'Phone Number': c.mobile || '-',
        'Email Address': c.businessEmail || '-',
        'Pin Code': c.pinCode || '-',
        'Instagram Profile Link': c.profileLink || '-',
        'YouTube Profile Link': c.youtubeLink || '-',
        'Market': c.market || '-',
        'State': c.state || '-',
        'Remarks': c.remark || '-'
      }));
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');
      XLSX.writeFile(workbook, 'contact_submissions.xlsx');
    } catch (error) {
      console.log('Failed to export data')
    }
  };

  const handleAssignClick = (contactId) => {
    setSelectedContactId(contactId);
    setAssignModalOpen(true);
  };

  const handleUserAssign = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    try {
      await api.put(`/contacts/campaign/${selectedContactId}`, { assignedTo: selectedUserId });
      toast.success('Contact assigned successfully');
      setAssignModalOpen(false);
      setSelectedUserId('');
      setSelectedContactId('');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to assign contact');
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    try {
      await api.patch('/existing-influencers/bulk-assign', { ids: selectedRows, assignedTo: selectedUserId });
      toast.success(`${selectedRows.length} contact(s) assigned successfully`);
      setBulkAssignModalOpen(false);
      setSelectedUserId('');
      setSelectedRows([]);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to assign contacts');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const columns = [
    { field: 'sno', headerName: 'S.No', width: 80 },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'username', headerName: 'Username', width: 150 },
    { field: 'phoneNumber', headerName: 'Phone Number', width: 150 },
    { field: 'email', headerName: 'Email Address', width: 200 },
    { field: 'pinCode', headerName: 'Pin Code', width: 120 },
    { field: 'instagramLink', headerName: 'Instagram Profile Link', width: 200 },
    { field: 'youtubeLink', headerName: 'YouTube Profile Link', width: 200 },
    { field: 'market', headerName: 'Market', width: 150 },
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'remarks', headerName: 'Remarks', width: 200 },
  ];

  if (activeTab !== 'pending') {
    columns.push({
      field: 'assignedTo',
      headerName: 'Assigned To',
      width: 150
    });
  }

  if (activeTab === 'pending') {
    columns.push({
      field: 'actions',
      headerName: 'Action',
      width: 120,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => handleAssignClick(params.row._id)}
        >
          Assign
        </Button>
      )
    });
  }

  const rows = contacts.map((contact, index) => ({
    id: contact._id,
    sno: index + 1,
    name: contact.name || '-',
    username: contact.username || '-',
    phoneNumber: contact.mobile || '-',
    email: contact.businessEmail || '-',
    pinCode: contact.pinCode || '-',
    instagramLink: contact.profileLink || '-',
    youtubeLink: contact.youtubeLink || '-',
    market: contact.market || '-',
    state: contact.state || '-',
    remarks: contact.remark || '-',
    assignedTo: contact.assignedTo ? (contact.assignedTo.name || contact.assignedTo.username) : '-',
    _id: contact._id
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Campaign name : {campaignName}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Link />}
            onClick={handleCopyLink}
          >
            Copy Form Link
          </Button>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={(e, val) => { setActiveTab(val); setSelectedRows([]); }} sx={{ mb: 2 }}>
        <Tab label="Pending" value="pending" />
        <Tab label="Assign" value="assigned" />
        <Tab label="Onboard" value="completed" />
      </Tabs>

      {activeTab === 'pending' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            variant="contained"
            disabled={selectedRows.length === 0}
            onClick={() => setBulkAssignModalOpen(true)}
          >
            Assign Selected {selectedRows.length > 0 ? `(${selectedRows.length})` : ''}
          </Button>
        </Box>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          autoHeight
          checkboxSelection={activeTab === 'pending'}
          onSelectionModelChange={(newSelection) => {
            if (activeTab === 'pending') {
              setSelectedRows(newSelection);
            }
          }}
          rowSelectionModel={activeTab === 'pending' ? selectedRows : []}
          getRowId={(row) => row.id}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              fontWeight: 600
            }
          }}
        />
      </Box>

      <Dialog open={assignModalOpen} onClose={() => setAssignModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign to User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserId}
              label="Select User"
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              {users.filter(user => user.role == "onboarder").map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name || user.username} - {user.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUserAssign} variant="contained">Assign</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkAssignModalOpen} onClose={() => setBulkAssignModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign {selectedRows.length} Selected to User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserId}
              label="Select User"
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              {users.filter(user => user.role == "onboarder").map((user) => (
                <MenuItem key={user._id} value={user._id}>
                  {user.name || user.username} - {user.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignModalOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkAssign} variant="contained">Assign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContactList;
